import {
  Activity,
  Button,
  Download,
  ExternalLink,
  FileCode,
  FileText,
  Mail,
  Mailbox,
  Server,
  UserCog,
  X,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface DocItem {
  id: string;
  titleKey: string;
  descriptionKey: string;
  href: string;
}

interface DevToolLink {
  id: string;
  titleKey: string;
  descriptionKey: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeKey?: string;
}

const DEV_TOOL_LINKS: DevToolLink[] = [
  {
    id: "portainer-nonprod",
    titleKey: "admin.devTools.items.portainerNonprod.title",
    descriptionKey: "admin.devTools.items.portainerNonprod.description",
    href: "https://portainer.nonprod.medecine360.com/",
    icon: Server,
    badgeKey: "admin.devTools.badges.nonprod",
  },
  {
    id: "portainer-prod",
    titleKey: "admin.devTools.items.portainerProd.title",
    descriptionKey: "admin.devTools.items.portainerProd.description",
    href: "https://portainer.prod.medecine360.com/",
    icon: Server,
    badgeKey: "admin.devTools.badges.prod",
  },
  {
    id: "Medecine360-status",
    titleKey: "admin.devTools.items.uptimeKuma.title",
    descriptionKey: "admin.devTools.items.uptimeKuma.description",
    href: "https://status.medecine360.com/status/prod",
    icon: Activity,
  },
  {
    id: "api-docs",
    titleKey: "admin.devTools.items.apiDocs.title",
    descriptionKey: "admin.devTools.items.apiDocs.description",
    href: "https://doc.api.medecine360.com/",
    icon: FileCode,
  },
  {
    id: "Jira-focus",
    titleKey: "admin.devTools.items.jiraFocus.title",
    descriptionKey: "admin.devTools.items.jiraFocus.description",
    href: "https://ews-jira.lovable.app",
    icon: FileCode,
  },
  {
    id: "notifications-prod",
    titleKey: "admin.devTools.items.notificationsProd.title",
    descriptionKey: "admin.devTools.items.notificationsProd.description",
    href: "https://notifications.ercowebsolutions.com/login",
    icon: Mail,
    badgeKey: "admin.devTools.badges.prod",
  },
  {
    id: "notifications-dev",
    titleKey: "admin.devTools.items.notificationsDev.title",
    descriptionKey: "admin.devTools.items.notificationsDev.description",
    href: "https://notifications.dev.ercowebsolutions.com/dashboard/email",
    icon: Mail,
    badgeKey: "admin.devTools.badges.nonprod",
  },
  {
    id: "mail-server",
    titleKey: "admin.devTools.items.mailServer.title",
    descriptionKey: "admin.devTools.items.mailServer.description",
    href: "https://mail.medecine360.com",
    icon: Mailbox,
  },
  {
    id: "crm-server",
    titleKey: "admin.devTools.items.crmServer.title",
    descriptionKey: "admin.devTools.items.crmServer.description",
    href: "https://crm.medecine360.com",
    icon: UserCog,
  },
];

export const DOC_ITEMS: DocItem[] = [
  {
    id: "doc1",
    titleKey: "admin.docs.items.doc1.title",
    descriptionKey: "admin.docs.items.doc1.description",
    href: "/Medecine360_Demo_Client.pptx",
  },
  {
    id: "doc2",
    titleKey: "admin.docs.items.doc2.title",
    descriptionKey: "admin.docs.items.doc2.description",
    href: "/Medecine360_Documentation_Technique.pptx",
  },
  {
    id: "doc3",
    titleKey: "admin.docs.items.doc3.title",
    descriptionKey: "admin.docs.items.doc3.description",
    href: "/Medecine360_Guide_Integration.pptx",
  },
  {
    id: "doc4",
    titleKey: "admin.docs.items.doc4.title",
    descriptionKey: "admin.docs.items.doc4.description",
    href: "/Medecine360_Parties_Prenantes.pptx",
  },
];

export const AdminDocumentation: React.FC = () => {
  const { t } = useTranslation();
  const [previewDoc, setPreviewDoc] = useState<DocItem | null>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (previewDoc) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [previewDoc]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewDoc(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const fileUrl = previewDoc
    ? `${window.location.origin}${previewDoc.href}`
    : "";
  const viewerUrl = fileUrl
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`
    : "";

  return (
    <>
      <div className="page-container">
        <div className="page-section">
          <h1 className="text-2xl font-semibold text-neutral-900">
            {t("admin.docs.title")}
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            {t("admin.docs.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 page-section md:grid-cols-2 lg:grid-cols-4">
          {DOC_ITEMS.map(doc => (
            <div
              key={doc.id}
              className="flex flex-col justify-between h-full p-6 bg-white border rounded-lg shadow-sm border-neutral-200"
            >
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-50">
                    <FileText className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-lg font-medium text-neutral-900">
                    {t(doc.titleKey)}
                  </h2>
                </div>
                <p className="text-sm text-neutral-600">
                  {t(doc.descriptionKey)}
                </p>
              </div>
              <div className="mt-4">
                <Button
                  className="flex items-center justify-center w-full gap-2"
                  onClick={() => setPreviewDoc(doc)}
                >
                  <FileText className="w-4 h-4" />
                  <span>{t("admin.docs.viewButton")}</span>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Developer Tools Section */}
        <div className="mt-10 page-section">
          <h2 className="text-xl font-semibold text-neutral-900">
            {t("admin.devTools.title")}
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            {t("admin.devTools.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 page-section md:grid-cols-2 lg:grid-cols-4">
          {DEV_TOOL_LINKS.map(tool => (
            <a
              key={tool.id}
              href={tool.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col justify-between h-full p-6 transition-all bg-white border rounded-lg shadow-sm group border-neutral-200 hover:border-primary-300 hover:shadow-md"
            >
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center justify-center w-10 h-10 transition-colors rounded-full bg-emerald-50 group-hover:bg-emerald-100">
                    <tool.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-neutral-900">
                      {t(tool.titleKey)}
                    </h3>
                    {tool.badgeKey && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          tool.badgeKey.includes("prod") &&
                          !tool.badgeKey.includes("nonprod")
                            ? "bg-danger-50 text-danger-700"
                            : "bg-warning-50 text-warning-700"
                        }`}
                      >
                        {t(tool.badgeKey)}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-neutral-600">
                  {t(tool.descriptionKey)}
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                <span>{t("admin.devTools.openLink")}</span>
                <ExternalLink className="w-4 h-4" />
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Full-screen preview modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white">
          {/* Header */}
          <div className="flex items-center justify-between flex-shrink-0 px-6 py-4 bg-white border-b border-neutral-200">
            <div className="flex items-center min-w-0 gap-3">
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-primary-50">
                <FileText className="w-4 h-4 text-primary-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold truncate text-neutral-900">
                  {t(previewDoc.titleKey)}
                </h2>
                <p className="text-xs truncate text-neutral-500">
                  {t(previewDoc.descriptionKey)}
                </p>
              </div>
            </div>
            <div className="flex items-center flex-shrink-0 gap-2 ml-4">
              <a
                href={previewDoc.href}
                download
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>{t("admin.docs.download")}</span>
              </a>
              <button
                type="button"
                onClick={() => setPreviewDoc(null)}
                className="flex items-center justify-center w-8 h-8 transition-colors rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100"
                aria-label={t("common.close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* iframe */}
          <div className="flex-1 overflow-hidden bg-neutral-100">
            {viewerUrl ? (
              <iframe
                key={previewDoc.id}
                title={t(previewDoc.titleKey)}
                src={viewerUrl}
                className="w-full h-full border-0"
                allowFullScreen
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-neutral-500">
                {t("admin.docs.viewer.loading")}
              </div>
            )}
          </div>

          {/* Footer notice */}
          <div className="flex-shrink-0 px-6 py-2 border-t border-neutral-100 bg-neutral-50">
            <p className="text-xs text-neutral-400">
              {t("admin.docs.viewer.notice")}
            </p>
          </div>
        </div>
      )}
    </>
  );
};
